import type { Sticker } from "./StickerType";
import type { AlmSourceId, InfoLevel, RiskType } from "./baseTypes";
import type { RelativeCoordinate } from "./coordinates";
import type { Link } from "./link";
import type { MarkMode } from "./markMode";
import type { LinkedObjective } from "./objective";
import type { DependencyTeamFilter } from "./search";
import type { Iteration, Team } from "./session";
import type { AlmItemStatus, Status, StatusClassDistribution } from "./status";
import type { StickyType } from "./stickyType";
import type { AuthUser } from "./user";

export interface BoardCard {
  data: Card;
  meta: CardMeta;
}

export interface PositionedCard {
  data: { id: string };
  meta: { pos: RelativeCoordinate; zIndex?: number };
}

export interface CardMeta {
  pos: RelativeCoordinate;
  zIndex: number;
  mark: MarkMode;
  isHighlighted: boolean;
  isRelatedToHighlighted: boolean;
  dependencyTeamFilter: DependencyTeamFilter | null;
  editing: boolean;
  dragging: boolean;
  shouldAnimate?: boolean;
}

interface BaseCard {
  id: string;
  almId?: string; // the ID of the external ALM item
  text: string;
  flagType: Sticker | null;
  status?: Status;
}

/**
 * Model that represents a card component on the UI.
 */
export interface Card extends BaseCard {
  groupId?: string | null;
  almIssueUrl?: string;
  almSourceId: AlmSourceId | null;
  points: number;
  priority: number;
  teamId: string | null;
  precondTeam?: Team; // team that created the sticky
  dependTeam?: Team; // team that the sticky depends on
  iterationId: number | null;
  editor: AuthUser | null;
  type: StickyType;
  risk?: RiskType;
  status?: AlmItemStatus;
  transition?: string;
  objectives: LinkedObjective[];
  reactions?: Reactions;
  artId: string | null;
  assignee: AuthUser | null;
  reporter: AuthUser | null;
  linkedStickies: LinkedSticky[];
  temporaryLinks: Link[]; // links that are not saved yet
  statusDistribution?: StatusClassDistribution;
} //TODO subtypes depending on type.functionality

export interface ResolvedCard extends BaseCard {
  groupId?: string;
  type: StickyType;
  iteration?: Iteration;
  team?: Team;
  artId?: string;
  points: number;
  precondTeam?: Team;
  dependTeam?: Team;
}

export interface SearchResult extends BaseCard {
  boardId: string;
  typeId: string;
  teamId?: string;
  artId?: string;
  iterationId?: number;
  almIssueUrl?: string;
  groupId?: string;
  dependTeamId?: string;
  precondTeamId?: string;
  points?: number;
  priority?: number;
  objectives?: LinkedObjective[];
}

export type Reactions = Record<Reaction, AuthUser[]>;

export const reactions = ["hot", "love", "increment"] as const;

export type Reaction = (typeof reactions)[number];

const reactionLabels: Record<Reaction, string> = {
  hot: /*$t*/ "label.reaction.hot",
  increment: /*$t*/ "label.reaction.increment",
  love: /*$t*/ "label.reaction.love",
};

export function getReactionLabel(reaction: Reaction) {
  return reactionLabels[reaction];
}

export interface LinkedSticky {
  stickyId: string;
  linkId: string;
  direction: "from" | "to";
  state: InfoLevel;
}
